import { FormEvent, useCallback, useMemo, useState } from 'react'
import { getCounterClockwisePoints } from '~/components/DrawingCanvas/Walls/util'
import { onCreateLShape } from '~/config/analytics'
import LayerKeys from '~/config/layerKeys'
import { DEFAULT_WALL_SIZES } from '~/config/wallSizes'
import { trackEvent } from '~/lib/analytics'
import { guid } from '~/lib/utils'
import { start2DFit } from '~/store/camera'
import { useAppDispatch } from '~/store/hooks'
import { addObject } from '~/store/objects'
import { DenormalizedWall } from '~/store/objects/types'
import { Button } from '~/ui/Button'
import { DimensionInput } from '~/ui/DimensionInput'
import { Form } from '~/ui/Form'
import { Icon } from '~/ui/Icon'

const POSITIONS_KEYS = ['A', 'B', 'C', 'D'] as const

const POSITIONS: Record<
  'A' | 'B' | 'C' | 'D',
  {
    translate: string
    scale: [string, string]
    yFlipped?: { translate: string }
    xFlipped?: { translate: string }
    xyFlipped?: { translate: string }
  }
> = {
  A: {
    translate: '0, 110',
    scale: ['1', '-1'],
    yFlipped: {
      translate: '0, 110',
    },
  },
  B: {
    translate: '60, 10',
    scale: ['-1', '-1'],
    xFlipped: {
      translate: '65, 10',
    },
    yFlipped: {
      translate: '60, 0',
    },
    xyFlipped: {
      translate: '60, 0',
    },
  },
  C: {
    translate: '190, 140',
    scale: ['-1', '1'],
    xFlipped: {
      translate: '200, 140',
    },
  },
  D: {
    translate: '95, 200',
    scale: ['-1', '1'],
    xFlipped: {
      translate: '95, 200',
    },
  },
}

export default function LShapeScreen({ handleClose }: { handleClose(): void }) {
  const [sideA, setSideA] = useState(432)
  const [sideB, setSideB] = useState(216)
  const [sideC, setSideC] = useState(168)
  const [sideD, setSideD] = useState(624)
  const [height, setHeight] = useState(288)
  const [xFlip, setXFlip] = useState(false)
  const [yFlip, setYFlip] = useState(false)
  const dispatch = useAppDispatch()

  const onSubmit = useCallback(
    (ev: FormEvent<HTMLFormElement>) => {
      ev.preventDefault()
      trackEvent(onCreateLShape())

      const thickness = DEFAULT_WALL_SIZES.EXTERIOR_WALLS.thickness

      const outsideSideA = sideA + thickness
      const outsideSideB = sideB + thickness
      const outsideSideC = sideC + thickness
      const outsideSideD = sideD + thickness

      let perimeterPoints = [
        { x: 0, y: outsideSideA - outsideSideC },
        { x: -outsideSideB, y: outsideSideA - outsideSideC },
        { x: -outsideSideB, y: -outsideSideC },
        { x: outsideSideD - outsideSideB, y: -outsideSideC },
        { x: outsideSideD - outsideSideB, y: 0 },
        { x: 0, y: 0 },
        { x: 0, y: outsideSideA - outsideSideC },
      ]

      if (xFlip) {
        perimeterPoints = perimeterPoints.map(point => ({ ...point, x: point.x * -1 }))
      }
      if (yFlip) {
        perimeterPoints = perimeterPoints.map(point => ({ ...point, y: point.y * -1 }))
      }

      perimeterPoints = getCounterClockwisePoints(perimeterPoints)

      const wallId = guid()

      const wallModel: DenormalizedWall = {
        id: wallId,
        layerKey: LayerKeys.EXTERIOR_WALLS,
        segments: [],
        isEnclosed: true,
        roofSectionIds: [],
        position: { x: 0, y: 0, z: 0 },
      }

      perimeterPoints.forEach((point, i, arr) => {
        const isLast = i === arr.length - 1
        if (isLast) return
        const nextPoint = arr[i + 1]
        wallModel.segments.push({
          id: guid(),
          parentId: wallId,
          layerKey: LayerKeys.EXTERIOR_WALLS,
          thickness: DEFAULT_WALL_SIZES.EXTERIOR_WALLS.thickness,
          height,
          startPoint: { x: point.x, y: point.y, z: 0 },
          endPoint: { x: nextPoint.x, y: nextPoint.y, z: 0 },
          materialIndex: 0,
          isFullHeight: false,
        })
      })

      wallModel.position = { ...wallModel.segments[0].startPoint }

      dispatch(addObject({ object: wallModel }))
      dispatch(start2DFit())
      handleClose()
    },
    [sideA, sideB, sideC, sideD, height, xFlip, yFlip, handleClose]
  )

  const [a, b, c, d] = useMemo(() => {
    return POSITIONS_KEYS.map(position => {
      const [xDirection, yDirection] = POSITIONS[position].scale

      let coordinates = POSITIONS[position].translate

      if (xFlip && yFlip && POSITIONS[position].xyFlipped) {
        coordinates = POSITIONS[position].xyFlipped.translate
      }

      if (xFlip && POSITIONS[position].xFlipped) {
        coordinates = POSITIONS[position].xFlipped.translate
      }

      if (yFlip && POSITIONS[position].yFlipped) {
        coordinates = POSITIONS[position].yFlipped.translate
      }

      const x = xFlip && xDirection === '-1' ? '-1' : '1'
      const y = yFlip && yDirection === '-1' ? '-1' : '1'

      return `translate(${coordinates}) scale(${x}, ${y})`
    })
  }, [xFlip, yFlip])

  return (
    <div className="grid grid-cols-2 gap-4">
      <Form onSubmit={onSubmit}>
        <DimensionInput label="Side A" showStepperButtons value={sideA} onCommit={setSideA} />

        <DimensionInput label="Side B" showStepperButtons value={sideB} onCommit={setSideB} />

        <DimensionInput label="Side C" showStepperButtons value={sideC} onCommit={setSideC} />

        <DimensionInput label="Side D" showStepperButtons value={sideD} onCommit={setSideD} />

        <DimensionInput label="Height" showStepperButtons value={height} onCommit={setHeight} />

        <i>If the roof is sloped, enter eave height</i>

        <Button type="submit">Create L-Shape</Button>
      </Form>
      <div className="flex flex-col justify-center items-center gap-4">
        <svg
          width="200"
          height="200"
          viewBox="0 0 200 200"
          style={{ transform: `scaleX(${xFlip ? '-1' : '1'}) scaleY(${yFlip ? '-1' : '1'})` }}
          display="inline-flex"
        >
          <path d="M103.6 95.8V21.6H21.9v155.8h155.8V95.8h-74.1zm66.4 73.8H29.7V29.4h66v74.2H170v66z" />
          <text transform={a}>A</text>
          <text transform={b}>B</text>
          <text transform={c}>C</text>
          <text transform={d}>D</text>
        </svg>

        <div className="grid grid-cols-2 gap-4">
          <Button variant="secondary" onClick={() => setXFlip(old => !old)} className="flex flex-col items-center">
            <Icon name="swapHorizontal" />
            Flip Horizontal
          </Button>

          <Button variant="secondary" onClick={() => setYFlip(old => !old)} className="flex flex-col items-center">
            <Icon name="swapVertical" />
            Flip Vertical
          </Button>
        </div>
      </div>
    </div>
  )
}
